import { Controller } from "@hotwired/stimulus"
import {
  createChart,
  createTextWatermark,
  CandlestickSeries,
  HistogramSeries,
  LineSeries
} from 'lightweight-charts'
import {
  calculateEMA,
  getDefaultMaColor,
  buildChartOptions,
  buildCandlestickOptions,
  buildVolumeSeriesOptions,
  buildVolumeData,
  buildWatermarkLines,
  buildMaSeriesOptions
} from '../utils/chart_utils'

// Controller for rendering charts for screenshot capture
// Sets window.chartReady = true when the chart is fully rendered
export default class extends Controller {
  static targets = ["chart"]
  static values = {
    data: Array,
    stock: Object,
    width: { type: Number, default: 800 },
    height: { type: Number, default: 400 },
    maConfig: { type: Array, default: [] },
    logScale: { type: Boolean, default: false }
  }

  connect() {
    // Mark as not ready initially
    window.chartReady = false

    // Wait for DOM to be ready
    requestAnimationFrame(() => {
      this.renderChart()
    })
  }

  disconnect() {
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  }

  renderChart() {
    if (!this.hasChartTarget || !this.dataValue || this.dataValue.length === 0) {
      console.error('Chart target or data not available')
      window.chartReady = true // Mark ready even on error so capture doesn't hang
      return
    }

    const chartOptions = buildChartOptions({
      width: this.widthValue,
      height: this.heightValue,
      logScale: this.logScaleValue,
      interactive: false,  // Disable scroll/scale for capture
      bottomMargin: 0.25
    })

    try {
      this.chart = createChart(this.chartTarget, chartOptions)

      // Add candlestick series
      const candlestickSeries = this.chart.addSeries(CandlestickSeries, buildCandlestickOptions())
      candlestickSeries.setData(this.dataValue)

      // Volume goes into its own pane (pane index 1) so it gets a dedicated
      // vertical region with its own scale — otherwise a single high-volume
      // spike compresses every other bar into invisibility in the captured
      // image, and the AI reports "volume data not visible in the chart".
      const volumePaneIndex = 1
      const volumeSeries = this.chart.addSeries(
        HistogramSeries,
        buildVolumeSeriesOptions(),
        volumePaneIndex
      )
      volumeSeries.setData(buildVolumeData(this.dataValue))

      // Give the volume pane ~25% of the chart height; price gets the rest.
      const panes = this.chart.panes()
      if (panes[volumePaneIndex]?.setHeight && this.heightValue) {
        panes[volumePaneIndex].setHeight(Math.round(this.heightValue * 0.25))
      }

      // Add moving averages if configured
      this.addMovingAverages(this.dataValue)

      // Add watermark with stock info
      if (this.stockValue) {
        this.addWatermark()
      }

      // Fit content
      this.chart.timeScale().fitContent()

      // Mark as ready after a short delay to ensure rendering is complete
      setTimeout(() => {
        window.chartReady = true
        console.log('Chart ready for capture')
      }, 500)

    } catch (err) {
      console.error('Error rendering chart:', err)
      window.chartReady = true // Mark ready even on error
    }
  }

  addWatermark() {
    const lines = buildWatermarkLines(this.stockValue)
    if (lines.length === 0) return

    try {
      const pane = this.chart.panes()[0]
      createTextWatermark(pane, {
        horzAlign: 'center',
        vertAlign: 'center',
        lines: lines
      })
    } catch (err) {
      console.warn('Could not create watermark:', err)
    }
  }

  // Add moving averages based on configuration
  addMovingAverages(data) {
    const maConfig = this.maConfigValue
    if (!maConfig || maConfig.length === 0) return

    maConfig.forEach(config => {
      const period = config.period
      if (!period || data.length < period) return

      const color = config.color || getDefaultMaColor(period)
      const maData = calculateEMA(data, period)

      const maSeries = this.chart.addSeries(LineSeries, buildMaSeriesOptions({ color }))
      maSeries.setData(maData)
    })
  }

  // Expose calculateEMA for testing (delegates to shared utility)
  calculateEMA(data, period) {
    return calculateEMA(data, period)
  }

  // Expose getDefaultMaColor for testing (delegates to shared utility)
  getDefaultMaColor(period) {
    return getDefaultMaColor(period)
  }
}
